import { Capacitor } from '@capacitor/core'
import { getBillingCatalog } from './api'
import { initializeNativePurchases } from './platform'
import type { MembershipBillingPeriod } from './membership'
import {
  formatUsdPrice,
  subscriptionPlans,
  type MembershipPlan,
} from './subscriptionPolicy'
import { PRODUCT_IDS, productIdForOffer } from './storeProducts'

export { PRODUCT_IDS, productIdForOffer } from './storeProducts'

export type PaidMembershipPlan = Exclude<MembershipPlan, 'free'>
export type BillingPeriod = Exclude<MembershipBillingPeriod, null>
export type BillingPriceSource =
  | 'app-store'
  | 'play-store'
  | 'stripe'
  | 'policy'

export interface BillingOffer {
  plan: PaidMembershipPlan
  period: BillingPeriod
  price: number
  priceString: string
  currencyCode: string
  source: BillingPriceSource
  productId?: string
  checkoutUrl?: string
}

export interface BillingCatalog {
  source: BillingPriceSource
  offers: BillingOffer[]
  updatedAtMs: number
}

export const billingCatalogVersion = '2026-09-19.studio.1'

const paidPlans = subscriptionPlans.filter(
  (plan): plan is (typeof subscriptionPlans)[number] & {
    id: PaidMembershipPlan
  } => plan.id !== 'free',
)

export function policyBillingCatalog(): BillingCatalog {
  return {
    source: 'policy',
    updatedAtMs: Date.now(),
    offers: paidPlans.flatMap((plan) => [
      {
        plan: plan.id,
        period: 'monthly' as const,
        price: plan.monthlyPriceUsd,
        priceString: formatUsdPrice(plan.monthlyPriceUsd),
        currencyCode: 'USD',
        source: 'policy' as const,
        productId: productIdForOffer(plan.id, 'monthly'),
      },
      {
        plan: plan.id,
        period: 'yearly' as const,
        price: plan.annualPriceUsd,
        priceString: formatUsdPrice(plan.annualPriceUsd),
        currencyCode: 'USD',
        source: 'policy' as const,
        productId: productIdForOffer(plan.id, 'yearly'),
      },
    ]),
  }
}

function planFromProduct(value: string): PaidMembershipPlan | null {
  if (/studio/i.test(value)) return 'studio'
  if (/plus/i.test(value)) return 'plus'
  if (/pro/i.test(value)) return 'pro'
  return null
}

function periodFromProduct(value: string): BillingPeriod | null {
  if (value === PRODUCT_IDS.proMonthly || value === PRODUCT_IDS.studioMonthly) return 'monthly'
  if (/(annual|year|yearly|12m|p1y)/i.test(value)) return 'yearly'
  if (/(month|monthly|1m|p1m)/i.test(value)) return 'monthly'
  return null
}

async function nativeBillingCatalog(): Promise<BillingCatalog | null> {
  if (!Capacitor.isNativePlatform()) return null
  const platform = Capacitor.getPlatform()
  const publicKey =
    platform === 'ios'
      ? import.meta.env.VITE_REVENUECAT_IOS_PUBLIC_KEY
      : import.meta.env.VITE_REVENUECAT_ANDROID_PUBLIC_KEY
  if (!publicKey) return null

  await initializeNativePurchases()
  const { Purchases } = await import('@revenuecat/purchases-capacitor')
  const source = platform === 'ios' ? 'app-store' : 'play-store'
  const unique = new Map<string, BillingOffer>()

  // App Store ürünlerini doğrudan ürün kimliklerinden al. Böylece fiyat ve para
  // birimi RevenueCat offering sırasından bağımsız olarak StoreKit'ten gelir.
  if (platform === 'ios') {
    const { products } = await Purchases.getProducts({
      productIdentifiers: Object.values(PRODUCT_IDS),
    })
    for (const product of products) {
      const plan = planFromProduct(product.identifier)
      const period = periodFromProduct(product.identifier)
      if (!plan || !period || plan === 'plus') continue
      unique.set(`${plan}:${period}`, {
        plan,
        period,
        price: product.price,
        priceString: product.priceString,
        currencyCode: product.currencyCode,
        source,
        productId: product.identifier,
      })
    }
    return unique.size
      ? { source, offers: [...unique.values()], updatedAtMs: Date.now() }
      : null
  }

  const offerings = await Purchases.getOfferings()
  const packages = offerings.current?.availablePackages ?? []

  for (const item of packages) {
    const searchable = [
      item.identifier,
      item.packageType,
      item.offeringIdentifier,
      item.product.identifier,
      item.product.title,
      item.product.subscriptionPeriod,
    ].join(' ')
    const plan = planFromProduct(searchable)
    const period = periodFromProduct(searchable)
    if (!plan || !period) continue
    unique.set(`${plan}:${period}`, {
      plan,
      period,
      price: item.product.price,
      priceString: item.product.priceString,
      currencyCode: item.product.currencyCode,
      source,
      productId: item.product.identifier,
    })
  }

  if (!unique.size) return null
  return { source, offers: [...unique.values()], updatedAtMs: Date.now() }
}

export async function loadBillingCatalog(): Promise<BillingCatalog> {
  try {
    const native = await nativeBillingCatalog()
    if (native) return mergeWithFallback(native)
  } catch (error) {
    console.warn('[VOXNELA_BILLING] Mağaza fiyatları alınamadı.', error)
  }

  if (!Capacitor.isNativePlatform()) {
    try {
      const remote = await getBillingCatalog()
      if (
        remote.version === billingCatalogVersion &&
        remote.offers.length
      ) {
        return mergeWithFallback({
          source: remote.source === 'stripe' ? 'stripe' : 'policy',
          updatedAtMs: remote.updatedAtMs,
          offers: remote.offers,
        })
      }
    } catch (error) {
      console.warn('[VOXNELA_BILLING] Stripe fiyat kataloğu alınamadı.', error)
    }
  }

  return policyBillingCatalog()
}

function mergeWithFallback(catalog: BillingCatalog): BillingCatalog {
  const offers = new Map(
    policyBillingCatalog().offers.map((offer) => [
      `${offer.plan}:${offer.period}`,
      offer,
    ]),
  )
  for (const offer of catalog.offers) {
    offers.set(`${offer.plan}:${offer.period}`, offer)
  }
  return { ...catalog, offers: [...offers.values()] }
}

export function billingOffer(
  catalog: BillingCatalog,
  plan: PaidMembershipPlan,
  period: BillingPeriod,
): BillingOffer {
  return (
    catalog.offers.find(
      (offer) => offer.plan === plan && offer.period === period,
    ) ?? policyBillingCatalog().offers.find(
      (offer) => offer.plan === plan && offer.period === period,
    )!
  )
}
